import { useState, useRef, useEffect } from 'react'
import ReactPlayer from 'react-player'
import styles from './kdt-ugm-5th.module.scss'

interface IUgmCurriculumProps {
  curriculumDetailList: {
    id: number
    step: string
    title: string
    desc: string
    url: string
    contents: {
      contentsIntro?: string
      contentsInner?: string[]
    }[]
  }[]
}

const UgmCurriculumPc = ({ curriculumDetailList }: IUgmCurriculumProps) => {
  const [value, setValue] = useState(0)
  const selectedDetail = curriculumDetailList[value]
  const [isFirstTabInView, setIsFirstTabInView] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (value === 0) {
          setIsFirstTabInView(entry.isIntersecting)
        }
      },
      {
        threshold: 0.5,
      },
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current)
      }
    }
  }, [value])

  const onClickWeek = (id: number) => {
    setValue(id)
    if (id === 0) {
      setIsFirstTabInView(true)
    }
  }

  const shouldAutoplay = value === 0 ? isFirstTabInView : true

  return (
    <div className="flex w-full overflow-hidden rounded-lg bg-[#383838]" ref={containerRef}>
      <div className="w-[360px] shrink-0 px-7 py-8">
        <ul className="flex flex-col gap-3">
          {curriculumDetailList.map(detail => (
            <li key={detail.step}>
              <div
                className={`${
                  value === detail.id
                    ? 'border border-yellow-450 bg-[rgba(255,119,16,0.10)]'
                    : 'border border-[rgba(82,82,82,1)] bg-[rgba(23,23,23,0.30)]'
                } flex flex-1 cursor-pointer flex-col gap-1 rounded px-5 py-4`}
                onClick={() => onClickWeek(detail.id)}
              >
                <p
                  className={`${
                    value === detail.id ? 'text-yellow-450' : 'text-[rgba(255,119,16,0.5)]'
                  } text-base font-bold`}
                >
                  {detail.step}
                </p>
                <h6
                  className={`${
                    value === detail.id ? 'text-white' : 'text-[rgba(255,255,255,0.5)]'
                  } whitespace-pre-line text-xl font-bold leading-snug`}
                >
                  {detail.title}
                </h6>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex-1 px-7 py-8">
        <div className="w-full">
          <div className="mb-6">
            <p className="text-2xl font-bold leading-snug text-yellow-450">{selectedDetail.step}</p>
            <h6 className="mb-6 text-2xl font-bold leading-snug text-white">
              {selectedDetail.title}
            </h6>
            <p className="mb-6 text-lg text-white">{selectedDetail.desc}</p>

            <div
              className="w-full overflow-hidden rounded"
              style={{
                position: 'relative',
                paddingTop: '49.88%',
              }}
            >
              <ReactPlayer
                url={selectedDetail.url}
                width="100%"
                height="100%"
                controls={false}
                muted={true}
                playing={shouldAutoplay}
                playsInLine
                playsinline={true}
                loop
                config={{
                  file: {
                    attributes: {
                      autoPlay: shouldAutoplay,
                      muted: true,
                      playsInline: true,
                      controls: false,
                    },
                  },
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
                alt="-"
              />
            </div>
          </div>
          <div className="rounded bg-[#2F2F2F] px-6 py-5">
            {selectedDetail.contents.map(content => (
              <ul
                key={content.contentsIntro || 'content'}
                className="flex flex-col border-b border-[#404040] py-5 last:border-0 first-of-type:pt-0 last-of-type:pb-0"
              >
                {content.contentsIntro && (
                  <li className="mb-2 text-base font-semibold text-white only-of-type:mb-0">
                    {content.contentsIntro}
                  </li>
                )}
                {content.contentsInner?.map(el => (
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_section7} mb-0.5 whitespace-pre-line text-base leading-normal text-label-assistive last-of-type:mb-0`}
                    key={el}
                  >
                    {el}
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UgmCurriculumPc
